import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  ArrowUpRight, ChevronLeft, ChevronRight, Download, Eye, File, FileArchive, FileText, Folder, Globe, ImageOff, Sheet as SheetIcon,
  AudioLines, X,
} from "lucide-react";
import { isRemoteURL, isServerPath, mediaExtension, mediaFileName, type MediaRef } from "@timibot/core";
import { useApp } from "../../context";
import { mediaCache } from "../../state/media";
import { copyText } from "../../ui/clipboard";
import { Menu, Spinner, useLongPress, useMenu } from "../../ui/components";
import "./media.css";

/**
 * 一组媒体（对应 iOS 的 MediaGallery）：单张图按原比例显示，多张图排成网格；视频直接在页面里播放；其它文件是一行文件条。
 * 点图片进入全屏预览（左右切换、下载）。
 */
export function MediaGallery({ refs, align = "start" }: { refs: MediaRef[]; align?: "start" | "end" }) {
  const images = refs.filter((r) => r.kind === "image");
  const videos = refs.filter((r) => r.kind === "video");
  const others = refs.filter((r) => r.kind === "audio" || r.kind === "file");
  const [viewing, setViewing] = useState<number | null>(null);
  const grid = images.length > 1;
  const side = images.length > 4 ? 96 : 132;

  return (
    <div className={`gallery ${align}`}>
      {images.length > 0 && (
        <div className={grid ? "gallery-grid" : ""} style={grid ? { gridTemplateColumns: `repeat(${images.length > 4 ? 3 : 2}, ${side}px)` } : undefined}>
          {images.map((ref, i) => <ImageTile key={ref.source} refItem={ref} side={grid ? side : undefined} onOpen={() => setViewing(i)} />)}
        </div>
      )}
      {videos.map((ref) => <VideoTile key={ref.source} refItem={ref} />)}
      {others.map((ref) => ref.kind === "audio" ? <AudioTile key={ref.source} refItem={ref} /> : <FileChip key={ref.source} refItem={ref} />)}
      {viewing !== null && <Lightbox refs={images} index={viewing} onIndex={setViewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

function useMediaURL(ref: MediaRef) {
  const app = useApp();
  const [url, setURL] = useState<string | undefined>(() => mediaCache.cachedURL(ref));
  const [error, setError] = useState<string>();
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (url) return;
    let cancelled = false;
    setError(undefined);
    mediaCache.url(ref, app.api).then(
      (u) => { if (!cancelled) setURL(u); },
      (e) => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)); },
    );
    return () => { cancelled = true; };
  }, [ref.source, attempt, app.api]); // eslint-disable-line react-hooks/exhaustive-deps
  return { url, error, retry: () => setAttempt((n) => n + 1) };
}

function MediaMenu({ refItem, menu }: { refItem: MediaRef; menu: ReturnType<typeof useMenu> }) {
  const app = useApp();
  return (
    <Menu
      open={menu.open}
      anchor={menu.anchor}
      onClose={menu.close}
      align="start"
      width={200}
      items={[
        { label: "下载", icon: <Download size={16} />, onSelect: () => { mediaCache.save(refItem, app.api).catch((e) => app.showToast(String(e?.message ?? e))); } },
        ...(isRemoteURL(refItem) ? [{ label: "拷贝链接", icon: <Globe size={16} />, onSelect: () => { void copyText(refItem.source); app.showToast("已拷贝"); } }] : []),
        ...(isServerPath(refItem) ? [{ label: "拷贝服务器路径", icon: <Folder size={16} />, onSelect: () => { void copyText(refItem.source); app.showToast("已拷贝"); } }] : []),
      ]}
    />
  );
}

/** 一张图片。side 为空时按原比例显示（最大 280×360），否则裁成正方形。 */
function ImageTile({ refItem, side, onOpen }: { refItem: MediaRef; side?: number; onOpen: () => void }) {
  const { url, error, retry } = useMediaURL(refItem);
  const menu = useMenu();
  const press = useLongPress((pos) => menu.show(pos));
  const box = side ? { width: side, height: side } : undefined;
  return (
    <>
      <button
        className={`media-tile${side ? " square" : ""}`}
        style={box ?? (url ? undefined : { width: 220, height: 160 })}
        onClick={() => (url ? onOpen() : error ? retry() : undefined)}
        aria-label={`图片 ${mediaFileName(refItem)}`}
        {...press}
      >
        {url ? <img src={url} alt="" loading="lazy" draggable={false} />
          : error ? (
            <span className="media-error t-caption2 t-2"><ImageOff size={20} /><span>{error}</span><span className="t-3">点击重试</span></span>
          ) : <Spinner />}
      </button>
      <MediaMenu refItem={refItem} menu={menu} />
    </>
  );
}

function VideoTile({ refItem }: { refItem: MediaRef }) {
  const { url, error, retry } = useMediaURL(refItem);
  const menu = useMenu();
  const press = useLongPress((pos) => menu.show(pos));
  return (
    <>
      <div className="media-video" {...press}>
        {url ? <video src={url} controls playsInline preload="metadata" />
          : error ? <button className="media-error t-caption2 t-2" onClick={retry}><ImageOff size={20} />{error}<span className="t-3">点击重试</span></button>
          : <Spinner />}
      </div>
      <MediaMenu refItem={refItem} menu={menu} />
    </>
  );
}

function AudioTile({ refItem }: { refItem: MediaRef }) {
  const { url } = useMediaURL(refItem);
  return (
    <div className="file-chip audio">
      <span className="file-chip-icon"><AudioLines size={18} /></span>
      <span className="file-chip-text">
        <span className="t-footnote medium nowrap">{mediaFileName(refItem)}</span>
        {url ? <audio src={url} controls preload="metadata" /> : <span className="t-caption2 t-2">音频 · {mediaExtension(refItem).toUpperCase()}</span>}
      </span>
    </div>
  );
}

/** 非图片/视频的文件：一行，点一下在新标签页打开（浏览器能直接看 PDF、文本），右键可以下载 */
function FileChip({ refItem }: { refItem: MediaRef }) {
  const app = useApp();
  const [busy, setBusy] = useState(false);
  const menu = useMenu();
  const press = useLongPress((pos) => menu.show(pos));
  const ext = mediaExtension(refItem);
  const Icon = ext === "pdf" || ["md", "txt", "json", "yaml", "yml"].includes(ext) ? FileText
    : ["zip", "tar", "gz", "tgz", "7z", "rar"].includes(ext) ? FileArchive
    : ["xlsx", "xls", "csv", "tsv", "numbers"].includes(ext) ? SheetIcon
    : ["html", "htm"].includes(ext) ? Globe : File;
  const open = async () => {
    if (isRemoteURL(refItem)) { window.open(refItem.source, "_blank", "noopener"); return; }
    setBusy(true);
    // 先开窗口再填地址：异步之后再 window.open 会被浏览器当成弹窗拦掉
    const win = window.open("", "_blank");
    try {
      const url = await mediaCache.url(refItem, app.api);
      if (win) win.location.href = url; else await mediaCache.save(refItem, app.api);
    } catch (e) {
      win?.close();
      app.showToast(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <button className="file-chip" onClick={open} {...press}>
        <span className="file-chip-icon"><Icon size={18} /></span>
        <span className="file-chip-text">
          <span className="t-footnote medium nowrap">{mediaFileName(refItem)}</span>
          <span className="t-caption2 t-2">{ext ? `${ext.toUpperCase()} 文件` : "文件"}</span>
        </span>
        {busy ? <Spinner size={14} /> : isRemoteURL(refItem) ? <ArrowUpRight size={14} className="t-3" /> : <Eye size={14} className="t-3" />}
      </button>
      <MediaMenu refItem={refItem} menu={menu} />
    </>
  );
}

/** 全屏看图：左右切换、下载、Esc 关闭 */
function Lightbox({ refs, index, onIndex, onClose }: { refs: MediaRef[]; index: number; onIndex: (i: number) => void; onClose: () => void }) {
  const app = useApp();
  const ref = refs[index];
  const { url } = useMediaURL(ref);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && index > 0) onIndex(index - 1);
      if (e.key === "ArrowRight" && index < refs.length - 1) onIndex(index + 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, refs.length, onClose, onIndex]);
  return createPortal(
    <div className="lightbox" onClick={onClose}>
      <div className="lightbox-bar" onClick={(e) => e.stopPropagation()}>
        <button className="glass-button" aria-label="关闭" onClick={onClose}><X size={20} /></button>
        <span className="t-footnote nowrap" style={{ color: "#fff" }}>{refs.length > 1 ? `${index + 1} / ${refs.length}` : mediaFileName(ref)}</span>
        <button className="glass-button" aria-label="下载" onClick={() => mediaCache.save(ref, app.api).catch(() => {})}><Download size={18} /></button>
      </div>
      {url ? <img src={url} alt="" onClick={(e) => e.stopPropagation()} /> : <Spinner size={24} />}
      {index > 0 && <button className="lightbox-nav left" aria-label="上一张" onClick={(e) => { e.stopPropagation(); onIndex(index - 1); }}><ChevronLeft size={28} /></button>}
      {index < refs.length - 1 && <button className="lightbox-nav right" aria-label="下一张" onClick={(e) => { e.stopPropagation(); onIndex(index + 1); }}><ChevronRight size={28} /></button>}
    </div>,
    document.body,
  );
}
